// @flow
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage, intlShape } from 'react-intl';
import classNames from 'classnames/bind';
import Modal from '../../../../../elements/Modal/Modal';
import Loading from '../../../../../elements/Loading/Loading';
import { FormValidator } from '../../../../../utils/form';
import { FBLogin } from '../../../../../utils/facebook';
import { getBrowserLanguage } from '../../../../local-values/utils/countries';

import style from './FacebookNeedMail.scss';
import buttonStyle from '../../../../../assets/styles/button.scss';
import inputStyle from '../../../../../assets/styles/input.scss';
import formStyle from '../../../../../assets/styles/form.scss';
import yogi from '../../../../../assets/img/home/yogi-qempo_2.png';
import yogiError from '../../../../../assets/img/yogi-400.png';

import type { HTTPError } from '../../../../../error';
import type { Language } from '../../../../../entities/language';
import type { FormValidationResult } from '../../../../../utils/form';
import type { FBLoginResponse } from '../../../../../utils/facebook';
import type { LocaleState } from '../../../../locale/duck';

type Props = {
  +isAuthing: boolean;
  +fbLogin: (country: ?string, language: Language, token: string,
             email?: string, redirectTo?: string) => void;
  +authError: HTTPError;
  +redirectTo?: string;
  +isOpen?: boolean;
  +authenticated?: boolean;
  +handleClose: () => void;
  +resetAuthState: () => void;
  +locale: LocaleState;
};

type State = {
  email: {
    value: string;
    errors?: string[];
  };
};

const messages = defineMessages({
  emailPlaceholder: {
    id: 'auth.email.placeholder',
  },
});

export default class FacebookNeedMail extends Component<Props, State> {
  formValidator: FormValidator;
  state = {
    email: {
      value: '',
    },
  };

  constructor(props: Props) {
    super(props);
    this.formValidator = new FormValidator({
      email: {
        required: 'required',
        emailFormat: 'emailFormat',
      }
    });
  }

  componentDidUpdate(prevProps: Props) {
    const { authError, resetAuthState, authenticated, handleClose } = this.props;
    if (authError && (prevProps.authError !== authError) && authError.code === 400 && authError.data) {
      if (authError.data.name === 'FacebookUserWithNoEmail') {
        resetAuthState();
      }
    }

    if (authenticated && (prevProps.authenticated !== authenticated)) {
      handleClose();
    }
  }

  handleChange(e: Event) {
    if (e.target instanceof HTMLInputElement) {
      const { name, value } = e.target;
      this.setState({ [name]: { value } });
    }
  }

  handleFocus(e: Event) {
    if (e.target instanceof HTMLInputElement) {
      const { name, value } = e.target;
      this.setState({ [name]: { value } });
    }
  }

  handleBlur(e: Event) {
    if (e.target instanceof HTMLInputElement) {
      const { name, value } = e.target;
      const errors = this.formValidator.validateField(name, value);
      if (errors.length && (errors[0] === 'emailFormat')) {
        this.setState({ [name]: { value, errors } });
      } else {
        this.setState({ [name]: { value } });
      }
    }
  }

  handleSubmit(e: Event) {
    e.preventDefault();
    const { fbLogin, redirectTo, locale } = this.props;
    const emailValue = this.state.email.value;
    const errors = this.formValidator.validateForm({
      email: emailValue,
    });
    if (errors.length) {
      errors.forEach((error: FormValidationResult) => {
        this.setState({
          [error.field]: {
            ...this.state[error.field],
            errors: error.errors,
          },
        });
      });
    } else {
      FBLogin(window).then((response: FBLoginResponse) => {
        if (response.status === 'connected' && response.authResponse) {
          const localeUpper = locale.locale ? locale.locale.toUpperCase() : '';
          const lang = localeUpper || getBrowserLanguage();
          fbLogin(locale.country, (lang: any), response.authResponse.accessToken, emailValue, redirectTo);
        }
      });
    }
  }

  builderScene() {
    const { email } = this.state;
    const { isAuthing, authenticated, authError } = this.props;
    if (authenticated) {
      return <Fragment>
        <img src={yogi}
             alt="salud yogi"/>
        <FormattedMessage id="modal.sendForgotPassword.success.title">{
          text => <h1>{text}</h1>
        }</FormattedMessage>
        <FormattedMessage id="modal.sendForgotPassword.success.text">{
          text => <p>{text}</p>
        }</FormattedMessage>
      </Fragment>
    } else if (authError && authError.data && authError.data.name !== 'FacebookUserWithNoEmail') {
      return <Fragment>
        <img src={yogiError}
             alt="Avocado yogi"/>
        <FormattedMessage id="httpErrors.fetch.500">{
          text => <p>{text}</p>
        }</FormattedMessage>
      </Fragment>
    } else {
      const emailError = email.errors && FormValidator.buildErrorLabel(email.errors, {
        emailFormat: 'auth.email.error.format',
        required: 'auth.email.error.required',
      });
      return (
        <form className={classNames(style.authForm, formStyle.formWrap)}
              onSubmit={this.handleSubmit.bind(this)}>
          <FormattedMessage id="modal.facebookUserWithNoEmail.title">{
            text => <h1>{text}</h1>
          }</FormattedMessage>
          <FormattedMessage id="modal.facebookUserWithNoEmail.text">{
            text => <p>{text}</p>
          }</FormattedMessage>
          <section className={classNames(formStyle.row, formStyle.inputRow, style.row)}>
            <FormattedMessage id="auth.email.label">{
              txt => (<label className={formStyle.label}>{txt}</label>)
            }</FormattedMessage>
            <input type="email"
                   className={classNames(inputStyle.input,
                     { [inputStyle.red]: email.errors && email.errors.length },
                     { [inputStyle.lightBlue]: !!email.value })}
                   onChange={this.handleChange.bind(this)}
                   onFocus={this.handleFocus.bind(this)}
                   onBlur={this.handleBlur.bind(this)}
                   disabled={isAuthing}
                   name="email"
                   placeholder={this.context.intl.formatMessage(messages.emailPlaceholder)}/>
            {emailError}
          </section>
          <FormattedMessage id="modal.sendForgotPassword.button">{
            txt => (
              <button
                type="submit"
                disabled={isAuthing}
                className={classNames(buttonStyle.btn, buttonStyle.lightBlue, style.submitBtn)}
                onClick={this.handleSubmit.bind(this)}>{txt}
              </button>
            )
          }</FormattedMessage>
        </form>
      )
    }
  }

  render() {
    const { isAuthing, isOpen, handleClose } = this.props;
    return (
      <Modal open={isOpen}
             width={544}
             height={616}
             handleClose={() => !isAuthing && handleClose()}>
        {isAuthing ? <Loading type="sk-three-bounce"
                              className={style.modalLoading}
                              number={3}/> :
          <section className={style.facebookNeedMail}>
            {this.builderScene()}
          </section>
        }
      </Modal>
    )
  }
}

FacebookNeedMail.propTypes = {
  isAuthing: PropTypes.bool,
  authError: PropTypes.object,
  fbLogin: PropTypes.func.isRequired,
  redirectTo: PropTypes.string,
  isOpen: PropTypes.bool,
  authenticated: PropTypes.bool,
  handleClose: PropTypes.func.isRequired,
  resetAuthState: PropTypes.func.isRequired,
  locale: PropTypes.object,
};

FacebookNeedMail.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/auth/auth-modals/facebook-need-email/component/FacebookNeedMail.jsx